/**
 * Helper para construir breadcrumbs basados en nombres de rutas
 */

/**
 * Normaliza un texto a formato título (primera letra mayúscula, resto minúsculas)
 * @param {string} text - Texto a normalizar
 * @returns {string} - Texto normalizado
 */
function normalizeLabel(text) {
   if (!text) return '';
   // Convertir a formato título: primera letra mayúscula, resto minúsculas
   return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

// Mapeo de nombres de módulos a etiquetas legibles (normalizados)
const moduleLabels = {
   cliente: 'Cliente',
   agro: 'Agro',
   art: 'Art',
   automotor: 'Automotor',
   caucion: 'Caución',
   creditos: 'Préstamos',
   hipoteca: 'Hipoteca',
   rc: 'Responsabilidad Civil',
   poliza: 'Pólizas',
   siniestros: 'Siniestros',
   bsa: 'Bsa',
   reportes: 'Reportes',
};

// Mapeo de nombres de vistas a etiquetas legibles
const viewLabels = {
   alta: 'Alta',
   'lote/alta': 'Agregar Lote',
   cotizaciones: 'Cotizaciones',
   emision: 'Emisión',
   'confirmacion-emision': 'Confirmación de Emisión',
   capitas: 'Cápitas',
   archivos: 'Archivos',
   pago: 'Pago',
   planes: 'Planes',
   consulta: 'Consulta',
   datosCaucion: 'Datos de Caución',
   formalidad: 'Formalidades de Caución',
   entidades: 'Entidades',
   usuarios: 'Usuarios',
   prospectos: 'Prospectos',
   terminos: 'Términos',
   descuentos: 'Descuentos',
   coberturas: 'Coberturas',
   buscar: 'Buscar Siniestro',
   denuncia: 'Denuncia de Siniestro',
   solicitudes: 'Solicitudes',
   locaciones: 'Locaciones',
   resumen: 'Resumen',
   informacion: 'Información',
   documentos: 'Documentos',
};

// Mapeo de IDs de sección a nombres (basado en useSectionConfig, normalizados)
const sectionLabels = {
   1: 'Agro',
   2: 'Art',
   3: 'Automotor',
   4: 'Caución',
   5: 'Préstamos',
   8: 'Hipoteca',
   9: 'Responsabilidad Civil',
};

/**
 * Encuentra una ruta navegable para un módulo dado
 * Verifica si la ruta padre tiene componente; si no, usa la primera ruta hija navegable
 * @param {Object} router - Instancia del router de Vue
 * @param {string} moduleName - Nombre del módulo
 * @returns {Object|null} - Objeto con name de ruta navegable o null
 */
function findNavigableRoute(router, moduleName) {
   if (!router || !moduleName) return null;

   // Intentar con el nombre del módulo
   try {
      const resolved = router.resolve({ name: moduleName });
      if (resolved?.name === moduleName && resolved.matched?.length > 0) {
         const lastMatched = resolved.matched[resolved.matched.length - 1];
         const hasComponent =
            lastMatched?.component ||
            (lastMatched?.components &&
               Object.keys(lastMatched.components).length > 0);
         if (hasComponent) {
            return { name: moduleName };
         }
      }
   } catch (e) {
      // La ruta no existe, buscar ruta hija
   }

   // Buscar primera ruta hija con componente (ordenada por nombre para consistencia)
   try {
      const allRoutes = router.getRoutes();
      const childRoutes = allRoutes.filter(
         (r) =>
            r.name &&
            typeof r.name === 'string' &&
            r.name.startsWith(`${moduleName}/`) &&
            r.name !== moduleName
      );
      const childRoute = childRoutes.sort((a, b) =>
         (a.name || '').localeCompare(b.name || '')
      )[0];

      if (childRoute?.name) {
         try {
            const resolved = router.resolve({ name: childRoute.name });
            if (resolved?.name === childRoute.name) {
               return { name: childRoute.name };
            }
         } catch (e) {
            // La ruta hija no es navegable
         }
      }
   } catch (e) {
      console.warn('Error al buscar ruta navegable:', e);
   }

   return null;
}

/**
 * Obtiene el nombre de la sección desde el store
 * @param {Object} store - Store de Vuex
 * @returns {string|null} - Nombre de la sección o null
 */
function getSectionName(store) {
   try {
      const idSeccion = store?.state?.poliza?.poliza?.id_seccion;
      if (idSeccion && sectionLabels[idSeccion]) {
         return sectionLabels[idSeccion];
      }
   } catch (error) {
      console.warn('Error al obtener sección del store:', error);
   }
   return null;
}

/**
 * Construye el breadcrumb basado en el nombre de la ruta
 * @param {string} routeName - Nombre de la ruta (ej: 'agro/lote/alta')
 * @param {Object} routeMeta - Metadatos de la ruta
 * @param {Object} store - Store de Vuex
 * @param {Object} router - Instancia del router de Vue (opcional)
 * @returns {Object} - Objeto con home e items para el componente Breadcrumb
 */
export function buildBreadcrumb(routeName, routeMeta = {}, store = null, router = null) {
   // Si no hay nombre de ruta, retornar solo home
   if (!routeName) {
      return {
         home: {
            icon: 'pi pi-home',
            route: { name: 'menu' },
         },
         items: [],
      };
   }

   // Rutas que no deben mostrar breadcrumb o solo mostrar home
   const excludedRoutes = ['menu', 'login', 'recover-password'];
   if (excludedRoutes.includes(routeName)) {
      return {
         home: {
            icon: 'pi pi-home',
            route: { name: 'menu' },
         },
         items: [],
      };
   }

   const items = [];
   const parts = routeName.split('/');

   // Si hay metadatos personalizados, usar esos
   if (routeMeta.breadcrumb?.label) {
      // Construir breadcrumb desde metadatos
      if (parts.length > 1) {
         // Agregar módulo padre si existe
         const moduleName = parts[0];
         if (moduleLabels[moduleName]) {
            const navigableRoute = router ? findNavigableRoute(router, moduleName) : null;
            items.push({
               label: moduleLabels[moduleName],
               route: navigableRoute,
            });
         }
      }
      // Agregar label personalizado
      items.push({
         label: routeMeta.breadcrumb.label,
         route: null, // Página actual
      });
   } else {
      // Construir breadcrumb desde el nombre de la ruta
      if (parts.length === 1) {
         // Ruta de módulo principal (ej: 'automotor')
         const moduleName = parts[0];
         if (moduleLabels[moduleName]) {
            items.push({
               label: moduleLabels[moduleName],
               route: null, // Página actual
            });
         } else {
            // Si no está en el mapeo, usar el nombre normalizado
            items.push({
               label: normalizeLabel(moduleName),
               route: null,
            });
         }
      } else if (parts.length === 2) {
         // Ruta módulo/vista (ej: 'cliente/alta', 'art/capitas')
         const moduleName = parts[0];
         const viewName = parts[1];

         // Agregar módulo con ruta navegable verificada
         if (moduleLabels[moduleName]) {
            const navigableRoute = router ? findNavigableRoute(router, moduleName) : null;
            items.push({
               label: moduleLabels[moduleName],
               route: navigableRoute,
            });
         }

         // Agregar vista con label normalizado
         const viewLabel = viewLabels[viewName] || normalizeLabel(viewName);
         items.push({
            label: viewLabel,
            route: null, // Página actual
         });

         // En automotor/emisión, añadir nombre de la compañía seleccionada al breadcrumb
         if (routeName === 'automotor/emision' && store) {
            const companiaNombre = store.getters['automotor/cotizacionesSeleccionadas']?.[0]?.nombreCompania;
            if (companiaNombre) {
               items.push({
                  label: companiaNombre,
                  route: null,
               });
            }
         }
      } else if (parts.length === 3) {
         // Ruta módulo/submodulo/vista (ej: 'agro/lote/alta')
         const moduleName = parts[0];
         const subModuleName = parts[1];
         const viewName = parts[2];

         // Agregar módulo con ruta navegable verificada
         if (moduleLabels[moduleName]) {
            const navigableRoute = router ? findNavigableRoute(router, moduleName) : null;
            items.push({
               label: moduleLabels[moduleName],
               route: navigableRoute,
            });
         }

         // Construir nombre de subvista
         const subViewPath = `${subModuleName}/${viewName}`;
         const subViewLabel = viewLabels[subViewPath] || viewLabels[viewName] || 
            `${normalizeLabel(subModuleName)} > ${normalizeLabel(viewName)}`;

         items.push({
            label: subViewLabel,
            route: null, // Página actual
         });
      }
   }

   // Solo agregar sección si es relevante y no está duplicada
   // La sección solo se agrega si la ruta actual está relacionada con esa sección
   // y el primer item del breadcrumb no coincide con la sección
   const sectionName = getSectionName(store);
   if (sectionName && items.length > 0) {
      const firstItem = items[0];
      // Solo agregar si:
      // 1. El primer item no coincide con la sección (evitar duplicados)
      // 2. La sección es diferente del módulo actual
      if (firstItem.label !== sectionName && 
          !items.some(item => item.label === sectionName)) {
         // Verificar que la ruta actual esté relacionada con esta sección
         const currentModule = parts[0];
         const sectionModuleMap = {
            'Agro': 'agro',
            'Art': 'art',
            'Automotor': 'automotor',
            'Caución': 'caucion',
            'Préstamos': 'creditos',
            'Hipoteca': 'hipoteca',
            'Responsabilidad Civil': 'rc',
         };
         
         // Solo agregar si el módulo actual coincide con la sección
         if (sectionModuleMap[sectionName] === currentModule) {
            items.unshift({
               label: sectionName,
               route: null,
            });
         }
      }
   }

   return {
      home: {
         icon: 'pi pi-home',
         route: { name: 'menu' },
      },
      items,
   };
}
